import { inject, Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import type { MainRequestFilesPayload } from '@core/types';
import { OFFLINE_SNAPSHOT_STORE } from './offline-snapshot-store.types';
import type {
  OfflineSnapshotEnvelope,
  OfflineSnapshotKind,
  RequestListSnapshot,
  RequestListSnapshotInput,
  RequestSummarySnapshot,
  StoredOfflineSnapshot,
} from './offline-snapshot.types';

const SCHEMA_VERSION = 2 as const;
const SNAPSHOT_KINDS: readonly OfflineSnapshotKind[] = [
  'request-list',
  'request-summary',
];
const SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;
const LIST_LIMIT = 10;
const SUMMARY_LIMIT = 30;

@Injectable({ providedIn: 'root' })
export class OfflineSnapshotRepository {
  private readonly store = inject(OFFLINE_SNAPSHOT_STORE);

  async saveRequestList(
    environment: string,
    userId: number,
    input: RequestListSnapshotInput,
  ): Promise<void> {
    const resourceKey = canonicalizeParams(input.params);

    await this.save(
      environment,
      userId,
      'request-list',
      resourceKey,
      input.response,
      LIST_LIMIT,
    );
  }

  getRequestList(
    environment: string,
    userId: number,
    params: Params,
  ): Promise<OfflineSnapshotEnvelope<RequestListSnapshot> | null> {
    return this.get(
      environment,
      userId,
      'request-list',
      canonicalizeParams(params),
    );
  }

  async saveRequestListMainFiles(
    environment: string,
    userId: number,
    params: Params,
    mainFiles: MainRequestFilesPayload,
  ): Promise<void> {
    const resourceKey = canonicalizeParams(params);
    const snapshot = await this.get<RequestListSnapshot>(
      environment,
      userId,
      'request-list',
      resourceKey,
    );

    if (!snapshot) {
      return;
    }

    await this.save(
      environment,
      userId,
      'request-list',
      resourceKey,
      { ...snapshot.payload, mainFiles },
      LIST_LIMIT,
    );
  }

  async saveRequestSummary(
    environment: string,
    userId: number,
    requestNumber: string,
    snapshot: RequestSummarySnapshot,
  ): Promise<void> {
    await this.save(
      environment,
      userId,
      'request-summary',
      requestNumber,
      snapshot,
      SUMMARY_LIMIT,
    );
  }

  getRequestSummary(
    environment: string,
    userId: number,
    requestNumber: string,
  ): Promise<OfflineSnapshotEnvelope<RequestSummarySnapshot> | null> {
    return this.get(environment, userId, 'request-summary', requestNumber);
  }

  async clearUser(environment: string, userId: number): Promise<void> {
    const records = await this.store.entries();
    const ownedRecords = records.filter(
      record =>
        record.environment === environment &&
        record.userId === userId &&
        SNAPSHOT_KINDS.includes(record.kind),
    );

    await Promise.all(
      ownedRecords.map(record => this.store.delete(record.key)),
    );
  }

  private async save<T>(
    environment: string,
    userId: number,
    kind: OfflineSnapshotKind,
    resourceKey: string,
    payload: T,
    limit: number,
  ): Promise<void> {
    const now = new Date();
    const record: OfflineSnapshotEnvelope<T> = {
      environment,
      expiresAt: new Date(now.getTime() + SNAPSHOT_TTL_MS).toISOString(),
      fetchedAt: now.toISOString(),
      key: createKey(environment, userId, kind, resourceKey),
      kind,
      lastAccessedAt: now.toISOString(),
      payload,
      resourceKey,
      schemaVersion: SCHEMA_VERSION,
      userId,
    };

    await this.store.put(record);
    await this.enforceLimit(environment, userId, kind, limit);
  }

  private async get<T>(
    environment: string,
    userId: number,
    kind: OfflineSnapshotKind,
    resourceKey: string,
  ): Promise<OfflineSnapshotEnvelope<T> | null> {
    const key = createKey(environment, userId, kind, resourceKey);
    const stored = await this.store.get(key);

    if (!isValidRecord(stored, environment, userId, kind, resourceKey)) {
      if (stored) {
        await this.store.delete(key);
      }

      return null;
    }

    if (Date.parse(stored.expiresAt) <= Date.now()) {
      await this.store.delete(key);

      return null;
    }

    const accessed: OfflineSnapshotEnvelope<T> = {
      ...(stored as OfflineSnapshotEnvelope<T>),
      lastAccessedAt: new Date().toISOString(),
    };

    await this.store.put(accessed);

    return accessed;
  }

  private async enforceLimit(
    environment: string,
    userId: number,
    kind: OfflineSnapshotKind,
    limit: number,
  ): Promise<void> {
    const records = (await this.store.entries())
      .filter(
        record =>
          record.environment === environment &&
          record.userId === userId &&
          record.kind === kind,
      )
      .sort(
        (left, right) =>
          Date.parse(right.lastAccessedAt) - Date.parse(left.lastAccessedAt),
      );

    await Promise.all(
      records.slice(limit).map(record => this.store.delete(record.key)),
    );
  }
}

function createKey(
  environment: string,
  userId: number,
  kind: OfflineSnapshotKind,
  resourceKey: string,
): string {
  return `${environment}:${userId}:${kind}:${resourceKey}`;
}

function canonicalizeParams(params: Params): string {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(params)
        .filter(([key]) => key !== 'timestamp')
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => [
          key,
          key === 'rqQuery' ? normalizeRqQuery(value) : value,
        ]),
    ),
  );
}

function normalizeRqQuery(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return sortObjectKeys(JSON.parse(value) as unknown);
  } catch {
    return value;
  }
}

function sortObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeys);
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, sortObjectKeys(item)]),
    );
  }

  return value;
}

function isValidRecord(
  record: StoredOfflineSnapshot | null,
  environment: string,
  userId: number,
  kind: OfflineSnapshotKind,
  resourceKey: string,
): record is StoredOfflineSnapshot {
  return (
    record?.schemaVersion === SCHEMA_VERSION &&
    record.environment === environment &&
    record.userId === userId &&
    record.kind === kind &&
    record.resourceKey === resourceKey
  );
}
